import React, { useState, useEffect } from 'react'
import { Layout, Typography, Card, Table, Button, message, Tag } from 'antd'
import { ArrowLeftOutlined, DownloadOutlined, FilterOutlined } from '@ant-design/icons'
import ExportJsonExcel from 'js-export-excel'
import axios from 'axios'
import { Link } from 'react-router-dom'
import { PieChart, BarChart, Pie, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend } from 'recharts';

const { Content } = Layout
const { Title, Paragraph } = Typography

const ShuDetail = (props) => {
  const { run } = props.match.params
  const [data, setData] = useState([])
  const [loading, setLoading] = useState(false)
  const [pathopoiesisData, setPathopoiesisData] = useState([])
  const [pathogenicBinNum, setPathogenicBinNum] = useState(0)

  useEffect(() => {
    const fetchDetail = async () => {
      setLoading(true)
      try {
        const res = await axios.get(`/api/pathogenic/sample/${run}/detail`)
        setData(res.data.map((item, idx) => ({ ...item, key: idx })))

        const pathoRes = await axios.get(`/api/sample/${run}/pathopoiesis`);
        
        const pathogenicBins = pathoRes.data.pathogenicBins+pathoRes.data.nonPathogenicBins || 0
        // 存数字用于顶部标题展示
        setPathogenicBinNum(pathogenicBins)

        setPathopoiesisData([
          { name: 'Pathogenic', value: pathoRes.data.pathogenicBins || 0 },
          { name: 'Non-Pathogenic', value: pathoRes.data.nonPathogenicBins || 0 }
        ]);

      } catch (err) {
        message.error('Failed to load data')
      } finally {
        setLoading(false)
      }
    }
    fetchDetail()
  }, [run])

  const columns = [
    { title: 'Bin ID', dataIndex: 'bin_id', key: 'bin_id', width: 120 },

    { title: 'Species', dataIndex: 'species', key: 'species', width: 250, ellipsis: true, },
    { title: 'Genus', dataIndex: 'genus', key: 'genus', width: 160, ellipsis: true },
    { title: 'Family', dataIndex: 'family', key: 'family', width: 160, ellipsis: true },
    { title: 'Order', dataIndex: 'order_taxon', key: 'order_taxon', width: 160, ellipsis: true },
    { title: 'Class', dataIndex: 'class_taxon', key: 'class_taxon', width: 140, ellipsis: true },
    { title: 'Phylum', dataIndex: 'phylum', key: 'phylum', width: 140, ellipsis: true },
    { title: 'Domain', dataIndex: 'domain', key: 'domain', width: 120, ellipsis: true },
    {
      title: 'GTDB Result',
      key: 'gtdbDownload',
      width: 150,
      render: (_, record) => (
        <a
          href={`/api/download/gtdb/${encodeURIComponent(run)}/${encodeURIComponent(record.bin_id)}`}
          download
        >
          <DownloadOutlined /> Download
        </a>
      ),
    },
    {
      title: 'Genome Assembly',
      key: 'genomeDownload',
      width: 180,
      render: (_, record) => (
        <a
          href={`/api/download/genome/${encodeURIComponent(run)}/${encodeURIComponent(record.bin_id)}`}
          download
        >
          <DownloadOutlined /> Download
        </a>
      ),
    },
  ]

  return (
    <Layout style={{ minHeight: '100vh', background: '#f0f2f5' }}>
      <Content style={{ padding: '24px 16px', maxWidth: 1400, margin: '0 auto' }}>

        <Card style={{ marginBottom: 20, borderRadius: 12 }} bodyStyle={{ padding: '16px 20px' }}>
          <Title level={2} style={{ margin: 0, color: '#002156', fontSize: 22 }}>
            Phylogenetic Analysis Detail
          </Title>
          <Paragraph style={{ marginTop: 12, marginBottom: 0, fontSize: 14 }}>
            Run: <strong style={{ color: '#1677ff' }}>{run}</strong>
            &nbsp;&nbsp;|&nbsp;&nbsp;
            Total high- and medium-quality bins found: <strong style={{ color: '#cf1322' }}>{pathogenicBinNum}</strong>
          </Paragraph>
        </Card>

        <Card style={{ marginBottom: 20, borderRadius: 12 }}>
          <Link to="/shu">
            <Button icon={<ArrowLeftOutlined />} style={{ marginBottom: 16 }}>
              Back
            </Button>
          </Link>
          <Table
            columns={columns}
            dataSource={data}
            loading={loading}
            // pagination={{ pageSize: 15, showTotal: (total) => `Total ${total} bins` }}
            pagination={{
              pageSize: 10,
              pageSizeOptions: [10], // 支持切换每页条数
              showSizeChanger: true, // 开启切换每页条数
              showTotal: (total) => `Total ${total} bins`
            }}
            scroll={{ x: 'max-content' }}
            bordered={false}
          />
        </Card>

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 16 }}>
          <Button type="primary" icon={<DownloadOutlined />} onClick={() => {
            if (!data.length) { message.warning('No data to export'); return }
            const exportData = data.map(d => ({
              'Bin ID': d.bin_id || '',
              Species: d.species || '',
              Genus: d.genus || '',
              Family: d.family || '',
              Order: d.order_taxon || '',
              Class: d.class_taxon || '',
              Phylum: d.phylum || '',
              Domain: d.domain || '',
            }))
            const option = { fileName: 'LRMrepo_Bin_' + run, datas: [{ sheetData: exportData, sheetName: 'Detail', sheetHeader: Object.keys(exportData[0]) }] }
            new ExportJsonExcel(option).saveExcel()
            message.success('Export successfully')
          }}>Export All to Excel</Button>
        </div>
        
        <div className="chart-container">
          <h2>Phylogenetic Topology</h2>
          <p style={{marginTop:12, fontSize:13, color:'#666'}}>
              Phylogenetic tree of high- and medium-quality bins
          </p>
          <div style={{border:'1px solid #e5e7eb', borderRadius:'8px', padding:'16px', background:'#fff'}}>
            {run && (
              <img
                src={`http://47.103.129.131:5001/svg/${run}.svg`}
                alt={`${run} habitat co-occurrence upset figure`}
                style={{width:'100%', maxWidth:'800px', display:'block', margin:'0 auto'}}
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextElementSibling.style.display = 'block';
                }}
              />
            )}
            <div style={{display:'none', textAlign:'center', padding:'40px', color:'#666'}}>
              Analysing...
            </div>
            
          </div>
        </div>

      </Content>
    </Layout>
  )
}

export default ShuDetail
